import React, { useState } from "react";
import PageHeading from "../../../components/PageHeading";
import { BackButton } from "../../../components/btn/Buttons";
import Button from "../../../components/btn/Button";
import Input from "../../../components/inputs/Input";
import { useSelector } from "react-redux";
import moment from "moment";

import store from "../../../redux/store";

import PageLoading from "../../../components/PageLoading";

import Loader from "../../../components/Loader";
import Table from "../../../components/table/Table";

import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { get_dayend_data } from "../../DayEnd/DayendDaySlice";

const VehicleDayWise = () => {
  const [date, setDate] = useState("");
  const [show, setShow] = useState(false);

  const { dayend_list, dayend_loading } = useSelector((state) => state.day_end);
  const { firm_name } = useSelector((state) => state.profile);

  const handle_load_data = () => {
    store.dispatch(get_dayend_data({ date: date, page: 1, limit: 10 }));
  };

  // Group data by VEHICLE_NO_IN and VEHICLE_NO_OUT
  const groupedData = React.useMemo(() => {
    if (!dayend_list || dayend_list.length === 0) return [];

    const groups = [];
    let currentGroup = null;

    dayend_list.forEach((item, index) => {
      const vehicleKey = `${item.VEHICLE_NO_IN || ""}_${
        item.VEHICLE_NO_OUT || ""
      }`;

      if (!currentGroup || currentGroup.vehicleKey !== vehicleKey) {
        currentGroup = {
          vehicleKey,
          vehicleNoIn: item.VEHICLE_NO_IN || "-",
          vehicleNoOut: item.VEHICLE_NO_OUT || "-",
          items: [],
        };
        groups.push(currentGroup);
      }

      currentGroup.items.push(item);
    });

    return groups;
  }, [dayend_list]);

  // Table headers
  const table_header = [
    "Godown Name",
    "Product Name",
    "Fulls",
    "Empties",
    "FULLS IN",
    "EMPTIES IN",
    "Vehicle No IN",
    "FULLS OUT",
    "EMPTIES OUT",
    "Vehicle No OUT",
  ];
  const excelPdfHeaders = {
    row1: [
      "",
      "",
      "QTY as per Invoice",
      "",
      "",
      "",
      "Qty As per Vehicle movement",
      "",
      "",
      "",
    ],
    row2: [
      "Godown Name",
      "Product Name",
      "Fulls",
      "Empties",
      "FULLS IN",
      "EMPTIES IN",
      "Vehicle No IN",
      "FULLS OUT",
      "EMPTIES OUT",
      "Vehicle No OUT",
    ],
  };

  // Process data for export with merge information
  const processDataForExport = () => {
    const rows = [];
    const merges = [];
    let currentRow = 0;

    groupedData?.forEach((group) => {
      group.items.forEach((item, itemIndex) => {
        rows.push([
          item.GODOWN_NAME || "-",
          item.PRODUCT_NAME || "-",
          item.FULLS || 0,
          item.EMPTIES || 0,
          item.FULLS_IN || 0,
          item.EMPTYS_IN || 0,
          item.VEHICLE_NO_IN || 0,
          item.FULLS_OUT || 0,
          item.EMPTYS_OUT || 0,
          item.VEHICLE_NO_OUT || 0,
        ]);

        currentRow++;
      });
    });

    return { rows, merges };
  };

  const exportPDFWithMerge = ({ rows, fileName, tableName }) => {
    const doc = new jsPDF("landscape", "pt", "A3");

    doc.setFontSize(14);
    doc.text(tableName, 40, 40);

    doc.autoTable({
      startY: 60,
      head: [excelPdfHeaders.row1, excelPdfHeaders.row2],
      body: rows,
      theme: "grid",
      styles: {
        fontSize: 8,
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [40, 116, 166],
        textColor: 255,
        fontStyle: "bold",
        align: "center",
      },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 100 },
      },
      didDrawCell: function (data) {
        // Merge effect (visual)
        if (data.row.index === 0) {
          if (data.column.index === 2) {
            data.cell.colSpan = 2;
          }
          if (data.column.index === 4) {
            data.cell.colSpan = 6;
          }
        }
      },
    });

    doc.save(`${fileName}.pdf`);
  };

  const export_pdf = () => {
    const { rows } = processDataForExport();

    exportPDFWithMerge({
      rows,
      tableName: `Day End Report - ${moment(date).format("DD/MM/YYYY")}`,
      fileName: `${firm_name}_DayEnd_${moment(date).format("DDMMYYYY")}`,
    });
  };

  const downloadExcelWithMerge = (data, fileName, merges = []) => {
    const worksheetData = [
      [fileName],
      excelPdfHeaders.row1,
      excelPdfHeaders.row2,
      ...data,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Day End");

    worksheet["!cols"] = Array(10).fill({ width: 18 });

    worksheet["!merges"] = [
      // Title
      { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },

      // Godown Name
      { s: { r: 1, c: 0 }, e: { r: 2, c: 0 } },
      // Product Name
      { s: { r: 1, c: 1 }, e: { r: 2, c: 1 } },

      // QTY as per Invoice
      { s: { r: 1, c: 2 }, e: { r: 1, c: 3 } },

      // Qty As per Vehicle movement
      { s: { r: 1, c: 4 }, e: { r: 1, c: 9 } },

      // Vehicle No IN
      ...merges.map((m) => ({
        s: { r: m.s.r + 3, c: m.s.c },
        e: { r: m.e.r + 3, c: m.e.c },
      })),
    ];

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;
    link.click();
  };

  const export_excel = () => {
    const { rows, merges } = processDataForExport();

    downloadExcelWithMerge(
      rows,
      `Day End Report - ${moment(date).format("DD/MM/YYYY")}`,
      merges
    );
  };

  return (
    <div className="container">
      <PageHeading
        title={"Godown Stock Recon"}
        right={
          <>
            <BackButton />
          </>
        }
      />
      <div style={{ height: "20px" }}></div>

      <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
        <Input
          mw={"150px"}
          type={"date"}
          max={moment(new Date()).format("YYYY-MM-DD")}
          value={date}
          disabled={dayend_loading}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />

        <Button
          title={"SEARCH"}
          onClick={() => {
            handle_load_data();
            setShow(true);
          }}
        />
      </div>

      {show &&
        (dayend_loading ? (
          <PageLoading />
        ) : (
          <div>
            <div
              className="epss-btn"
              style={{
                justifyContent: "flex-start",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <button
                style={{
                  padding: "0.5em 1em",
                  fontSize: ".88em",
                  backgroundColor: "#e9e9e9",
                }}
                onClick={export_excel}
                disabled={!dayend_list || dayend_list.length === 0}
              >
                Excel
              </button>
              <button
                style={{
                  padding: "0.5em 1em",
                  fontSize: ".88em",
                  backgroundColor: "#e9e9e9",
                }}
                onClick={export_pdf}
                disabled={!dayend_list || dayend_list.length === 0}
              >
                PDF
              </button>
            </div>

            <Table
              headers={
                <>
                  <tr className="header-row-1">
                    <th rowSpan="2" className="header-cyan">
                      Godown Name
                    </th>
                    <th rowSpan="2" className="header-cyan">
                      Product Name
                    </th>
                    <th colSpan="2" className="header-cyan">
                      QTY as per Invoice
                    </th>
                    <th colSpan="6" className="header-white">
                      Qty As per Vehicle movement
                    </th>
                  </tr>

                  {/* Second Header Row */}
                  <tr className="header-row-2">
                    <th className="header-cyan">Fulls</th>
                    <th className="header-cyan">Empties</th>
                    <th className="header-blue">FULLS IN</th>
                    <th className="header-blue">EMPTIES IN</th>
                    <th className="header-white">Vehicle No IN</th>
                    <th className="header-blue">FULLS OUT</th>
                    <th className="header-blue">EMPTIES OUT</th>
                    <th className="header-white">Vehicle No OUT</th>
                  </tr>
                </>
              }
              body={
                <>
                  {groupedData.length > 0 ? (
                    groupedData.map((group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        {group.items.map((item, itemIndex) => {
                          return (
                            <tr key={itemIndex}>
                              <td>{item.GODOWN_NAME}</td>
                              <td>{item.PRODUCT_NAME}</td>

                              <td>{item.FULLS || 0}</td>
                              <td>{item.EMPTIES || 0}</td>

                              <td>{item.FULLS_IN || 0}</td>
                              <td>{item.EMPTYS_IN || 0}</td>

                              <td>{item.VEHICLE_NO_IN || 0}</td>

                              <td>{item.FULLS_OUT || 0}</td>
                              <td>{item.EMPTYS_OUT || 0}</td>

                              <td>{item.VEHICLE_NO_OUT || 0}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="no-data">
                        No data available for the selected date
                      </td>
                    </tr>
                  )}
                </>
              }
            />
          </div>
        ))}
      {dayend_loading && !show && <Loader loading={dayend_loading && !show} />}
    </div>
  );
};

export default VehicleDayWise;
